import type { Backend } from '@freelancer/datastore/core';
import {
  getQueryParamValue,
  OrderByDirection,
} from '@freelancer/datastore/core';
import type { PrototyperPermissionAccessRoleMappingApi } from 'api-typings/canvas/canvas';
import type { PrototyperPermissionsCollection } from './prototyper-permissions.types';

export function prototyperPermissionsBackend(): Backend<PrototyperPermissionsCollection> {
  return {
    defaultOrder: {
      field: 'id',
      direction: OrderByDirection.ASC,
    },
    fetch: (authUid, ids, query, order) => ({
      endpoint: 'canvas/0.1/canvas/permission/',
      params: {
        /**
         * The fetch endpoint supports filtering by arrays (ids, canvas_ids, principal_ids, access_roles).
         * To keep things simple and avoid complexity in the model,
         * we only support fetching by single query parameters or by IDs for now.
         */
        ...(ids ? { ids } : {}), // Fetch by IDs if provided
        canvas_ids: getQueryParamValue(query, 'canvasId'),
        principal_type: getQueryParamValue(query, 'principalType')[0],
        principal_ids: getQueryParamValue(query, 'principalId'),
        access_roles: getQueryParamValue(query, 'accessRole'),
        limit: query?.searchQueryParams.limit,
        offset: query?.searchQueryParams.offset,
      },
    }),
    push: (authUid, prototyperPermission) => {
      if (
        !prototyperPermission.principals ||
        prototyperPermission.principals.length === 0
      ) {
        throw new Error('Principals array cannot be empty.');
      }
      return {
        endpoint: 'canvas/0.1/canvas/permission/',
        payload: {
          canvas_id: prototyperPermission.canvasId,
          /** List of principals to assign the access role to for the given canvas. */
          principals: prototyperPermission.principals,
          access_role: prototyperPermission.accessRole,
        },
      };
    },
    set: undefined,
    update: (authUid, prototyperPermission, originalPrototyperPermission) => {
      if (!originalPrototyperPermission.canvasId) {
        throw new Error(
          'Prototyper board/Canvas Id is required to update Prototyper permissions.',
        );
      }

      return {
        endpoint: 'canvas/0.1/canvas/permission/',
        method: 'PUT',
        payload: {
          canvas_id: originalPrototyperPermission.canvasId,
          /** List of principals to update the access role for the given canvas. */
          new_role_mappings:
            prototyperPermission.newRoleMappings as PrototyperPermissionAccessRoleMappingApi[],
        },
      };
    },
    remove: (authUid, id, originalPrototyperPermission) => ({
      /**
       * The DeleteRequestFactory currently only allows deleting one id at a time.
       * The API is designed to accept multiple ids in the future.
       */
      endpoint: `canvas/0.1/canvas/permission/?ids[]=${id}`,
      method: 'DELETE',
      payload: {},
    }),
  };
}
